import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { UserSettingLayoutContainer } from "../UserSettingLayoutContainer";
import {
    RemoI18n,
    RemoI18nLanguages,
    RemoI18nLanguagesEnum,
    useLanguage,
    useTranslate,
} from "@obsbot/remo-i18n";
import {
    FaceChangeIconPNG,
    TornadoIconPNG,
    WelcomeIconPNG,
    CryIconPNG,
    SettingSelector,
    SpecialEffectIconUsed,
    HeartIconPNG,
    BombIconPNG,
    SkateIconPNG,
    CelebrateIconPNG,
    SpecialEffectPreviewMap,
    SpecialEffectPreviewClassMap,
} from "remo-components";
import { ShowAnimationType, useGlobalStore, usePreferencesStore } from "@obsbot/remo-stores";
import "./index.less";
import { Avatar } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import classNames from "classnames";
import { useLottiePreview } from "remo-components";
import { eventBus } from "@obsbot/remo-utils";
import RemovePNG from "./remove.png";

export const ANIMATION_ID_TO_SRC: Record<SpecialEffectIconUsed, string> = {
    FaceChangeIcon: FaceChangeIconPNG,
    TornadoIcon: TornadoIconPNG,
    WelcomeIcon: WelcomeIconPNG,
    CryIcon: CryIconPNG,
    HeartIcon: HeartIconPNG,
    BombIcon: BombIconPNG,
    SkateIcon: SkateIconPNG,
    CelebrateIcon: CelebrateIconPNG,
    EmptyIcon: "",
};

const ANIMATION_LIST: Array<{ src: string; id: SpecialEffectIconUsed }> = [
    {
        src: FaceChangeIconPNG,
        id: "FaceChangeIcon",
    },
    {
        src: TornadoIconPNG,
        id: "TornadoIcon",
    },
    {
        src: WelcomeIconPNG,
        id: "WelcomeIcon",
    },
    {
        src: CryIconPNG,
        id: "CryIcon",
    },
    {
        src: HeartIconPNG,
        id: "HeartIcon",
    },
    {
        src: BombIconPNG,
        id: "BombIcon",
    },
    {
        src: SkateIconPNG,
        id: "SkateIcon",
    },
    {
        src: CelebrateIconPNG,
        id: "CelebrateIcon",
    },
];

const DisplaySettingPage = (): React.ReactElement => {
    const t = useTranslate();
    const globalStore = useGlobalStore();
    const preferenceStore = usePreferencesStore(globalStore.userInfo?.user_id ?? "")?.(
        state => state,
    );

    // 特效显示类型
    const [showAnimationType, setShowAnimationType] = useState<ShowAnimationType>(
        preferenceStore?.showAnimationType ?? ShowAnimationType.Default,
    );
    // 处理特效显示类型改变
    const onAnimationChanged = (value: ShowAnimationType): void => {
        setShowAnimationType(value);
        preferenceStore?.updateShowAnimationType(value);
    };
    // 特效配置
    const [showAnimations, setShowAnimations] = useState<string[]>(
        preferenceStore?.showAnimations ?? [],
    );
    useEffect(() => {
        if (preferenceStore.showAnimations?.length === 0) {
            preferenceStore.updateShowAnimations(new Array(3).fill("empty"));
            setShowAnimations(new Array(3).fill("empty"));
        }
    }, [preferenceStore]);
    const allIHaveAnimations = useMemo(() => {
        return ANIMATION_LIST;
    }, []);
    // 是否在修改特效配置
    const [isEditing, setIsEditing] = useState(false);
    // 当前的语言
    const curLanguage = useLanguage();
    // 处理语言改变
    const onLanguageChanged = useCallback(
        (lang: RemoI18nLanguages) => {
            RemoI18n.changeLanguage(lang);
            preferenceStore?.updateLanguage(lang);
        },
        [preferenceStore],
    );
    useEffect(() => {
        if (!isEditing) {
            setShowAnimations(preferenceStore?.showAnimations ?? []);
        }
    }, [isEditing, preferenceStore?.showAnimations]);
    // 处理特效的拖拽应用
    const [draggedKey, setDraggedKey] = useState<string>("");
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const isDraggingRef = useRef(false);
    const dragTargetRef = useRef<HTMLElement | null>(null);
    useEffect(() => {
        isDraggingRef.current = isDragging;
    }, [isDragging]);
    const handleFillToEmpty = (animation: string): void => {
        const emptyIndex = showAnimations.findIndex(item => item === "empty");
        if (emptyIndex === -1) {
            eventBus.emit("show-message", {
                message: t("settings.display-setting.effect-full"),
                type: "warning",
            });
        } else {
            const newShowAnimations = [...showAnimations];
            newShowAnimations[emptyIndex] = animation;
            setShowAnimations(newShowAnimations);
        }
    };
    const handleOnDrag = (): void => {
        if (!isDragging) {
            setIsDragging(true);
        }
    };
    const handleDragEnd = (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        // 使用辅助函数清理拖拽状态
        updateDragTarget(null);
        setDraggedKey("");
        setIsDragging(false);

        let target = document.elementFromPoint(e.clientX, e.clientY);
        const findEl = (): void => {
            while (target && target.localName !== "div") {
                target = target.parentElement;
            }
        };
        if (
            target?.localName === "svg" ||
            target?.localName === "path" ||
            target?.localName === "img"
        ) {
            findEl();
        }
        const attr_index = target?.getAttribute("data-index");
        if (target && attr_index !== undefined && attr_index !== null && draggedKey) {
            setShowAnimations(prevAnimations => {
                const newAnimations = [...prevAnimations];
                newAnimations[parseInt(attr_index)] = draggedKey;
                return newAnimations;
            });
        }
    };

    // 添加辅助函数处理拖拽目标的更新
    const updateDragTarget = (newTarget: HTMLElement | null): void => {
        // 如果新目标与当前目标相同，不做处理
        if (newTarget === dragTargetRef.current) {
            return;
        }

        // 清除旧目标的状态
        if (dragTargetRef.current) {
            dragTargetRef.current.classList.remove("drag-over");
        }

        // 更新新目标
        dragTargetRef.current = newTarget;
        if (newTarget) {
            newTarget.classList.add("drag-over");
        }
    };

    // 添加组件卸载时的清理函数
    useEffect(() => {
        return () => {
            updateDragTarget(null);
        };
    }, []);

    // 移除特效
    const removeAnimation = (animationIndex: number): void => {
        setShowAnimations(prevAnimations => {
            const newAnimations = [...prevAnimations];
            newAnimations[animationIndex] = "empty";
            return newAnimations;
        });
    };
    // 保存到仓库
    const handleSaveAnimations = (): void => {
        preferenceStore.updateShowAnimations(showAnimations);
        setIsEditing(false);
    };
    // 预览的特效
    const previewAnimations = useRef<Map<SpecialEffectIconUsed, HTMLDivElement | null>>(new Map());
    const { play, destroy, updateContainer } = useLottiePreview(undefined, () => {
        setDraggedKey("");
        setPreviewingAnimation(null);
    });
    const hoverTimer = useRef<NodeJS.Timeout | null>(null);
    const [previewingAnimation, setPreviewingAnimation] = useState<SpecialEffectIconUsed | null>(
        null,
    );
    // 处理预览特效
    const handleMouseEnter = (animation: SpecialEffectIconUsed): void => {
        const previewEl = previewAnimations.current.get(animation);
        if (!previewEl) {
            return;
        }
        updateContainer(previewEl.children[1] as HTMLElement);
        hoverTimer.current && clearTimeout(hoverTimer.current);
        hoverTimer.current = setTimeout(() => {
            if (isDraggingRef.current) {
                return;
            }
            setPreviewingAnimation(animation);
            play(animation);
        }, 500);
    };
    const handleMouseLeave = (): void => {
        setPreviewingAnimation(null);
        hoverTimer.current && clearTimeout(hoverTimer.current);
        destroy();
    };
    return (
        <UserSettingLayoutContainer>
            <div className="display-setting-container">
                <div className="display-setting-page">
                    <div className="setting-page-title">
                        {t("settings.display-setting.animationSetting")}
                    </div>
                    <div
                        className="setting-page-content"
                        style={{ height: (window as any).electron ? (isEditing ? 317 : 222) : 110 }}
                    >
                        <div className="setting-show-item-between">
                            <div className="setting-page-label">
                                {t("settings.display-setting.animationShowType")}
                            </div>
                            <div className="display-setting-page-input">
                                <SettingSelector
                                    key={showAnimationType}
                                    className="rtc-setting-selector"
                                    defaultValue={showAnimationType}
                                    options={Object.values(ShowAnimationType).map(value => ({
                                        label: t("settings.display-setting." + value),
                                        value: value as ShowAnimationType,
                                        title: "",
                                    }))}
                                    onChange={onAnimationChanged}
                                />
                            </div>
                        </div>
                        {(window as any).electron && (
                            <div
                                className="setting-show-item-start"
                                onDragOver={e => {
                                    e.preventDefault();
                                    e.dataTransfer.dropEffect = "copy";
                                }}
                            >
                                <div className="left">
                                    <div className="setting-page-label">
                                        {t("settings.display-setting.animationConfigure")}
                                    </div>
                                    <div className="setting-page-tips">
                                        {t("settings.display-setting.animationSettingTip")}
                                    </div>
                                    <div
                                        className="display-setting-animation-i-have"
                                        onDragLeave={e => {
                                            const container = e.currentTarget;
                                            const relatedTarget = e.relatedTarget as HTMLElement;
                                            // 只有在真正离开容器时才清除目标
                                            if (!container.contains(relatedTarget)) {
                                                updateDragTarget(null);
                                            }
                                        }}
                                        onDragOver={e => {
                                            const target = e.target as HTMLElement;
                                            const dropTarget = target.closest(
                                                ".display-setting-animation-item",
                                            ) as HTMLElement | null;

                                            // 更新拖拽目标
                                            updateDragTarget(dropTarget);
                                        }}
                                    >
                                        {showAnimations.map((animation, index) => (
                                            <div
                                                key={index}
                                                className={classNames(
                                                    "display-setting-animation-item",
                                                    SpecialEffectPreviewClassMap[
                                                        animation as SpecialEffectIconUsed
                                                    ],
                                                    {
                                                        "display-setting-animation-item-empty":
                                                            animation === "empty",
                                                        "display-setting-animation-item-empty-dragging":
                                                            animation === "empty" && isDragging,
                                                    },
                                                )}
                                                data-index={index}
                                                draggable={false}
                                                onDrag={e => {
                                                    e.preventDefault();
                                                    return false;
                                                }}
                                            >
                                                {animation === "empty" ? (
                                                    <PlusOutlined
                                                        style={{ color: "#999999", fontSize: 12 }}
                                                    />
                                                ) : (
                                                    <>
                                                        <Avatar
                                                            draggable={false}
                                                            size={34}
                                                            src={
                                                                ANIMATION_ID_TO_SRC[
                                                                    animation as SpecialEffectIconUsed
                                                                ]
                                                            }
                                                        />
                                                        <div
                                                            className="animation-item-remove"
                                                            style={{
                                                                opacity: isEditing ? 1 : 0,
                                                                pointerEvents: isEditing
                                                                    ? "auto"
                                                                    : "none",
                                                            }}
                                                            onClick={() => removeAnimation(index)}
                                                        >
                                                            <img src={RemovePNG} />
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    {isEditing && (
                                        <>
                                            <div
                                                className="setting-page-tips"
                                                style={{ marginTop: 18 }}
                                            >
                                                {t("settings.display-setting.animationIHave")}
                                            </div>
                                            <div className="display-setting-animation-list">
                                                {allIHaveAnimations.map((animation, index) => (
                                                    <div
                                                        key={index}
                                                        ref={ref => {
                                                            previewAnimations.current.set(
                                                                animation.id as SpecialEffectIconUsed,
                                                                ref,
                                                            );
                                                        }}
                                                        className={classNames(
                                                            "display-setting-animation-item",
                                                            SpecialEffectPreviewClassMap[
                                                                animation.id as SpecialEffectIconUsed
                                                            ],
                                                            "animation-" + animation.id,
                                                            {
                                                                "display-setting-animation-item-dragging":
                                                                    animation.id === draggedKey,
                                                            },
                                                        )}
                                                        onClick={() =>
                                                            handleFillToEmpty(animation.id)
                                                        }
                                                        onDrag={e => {
                                                            e.dataTransfer.effectAllowed = "all";
                                                            handleOnDrag();
                                                            setDraggedKey(animation.id);
                                                        }}
                                                        onDragEnd={handleDragEnd}
                                                        onMouseEnter={() =>
                                                            handleMouseEnter(
                                                                animation.id as SpecialEffectIconUsed,
                                                            )
                                                        }
                                                        onMouseLeave={handleMouseLeave}
                                                    >
                                                        <Avatar size={34} src={animation.src} />
                                                        <div
                                                            className={
                                                                "preview-content" +
                                                                (previewingAnimation ===
                                                                animation.id
                                                                    ? " preview-content-active"
                                                                    : "")
                                                            }
                                                            style={{
                                                                width: SpecialEffectPreviewMap[
                                                                    animation.id as SpecialEffectIconUsed
                                                                ],
                                                                height: SpecialEffectPreviewMap[
                                                                    animation.id as SpecialEffectIconUsed
                                                                ],
                                                            }}
                                                        ></div>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                                {isEditing ? (
                                    <div className="display-setting-animation-list-editing">
                                        <div className="cancel" onClick={() => setIsEditing(false)}>
                                            {t("cancel")}
                                        </div>
                                        <div className="done" onClick={handleSaveAnimations}>
                                            {t("done")}
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        className="display-setting-edit-button"
                                        onClick={() => setIsEditing(true)}
                                    >
                                        {t("settings.display-setting.edit")}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className="display-setting-page" style={{ marginTop: 32 }}>
                    <div className="setting-page-title">
                        {t("settings.display-setting.languageSetting")}
                    </div>
                    <div
                        className="setting-page-content"
                        style={{ height: 92, padding: "20px 60px 0" }}
                    >
                        <div className="setting-show-item-between">
                            <div className="setting-page-label">
                                {t("settings.display-setting.languageSelect")}
                            </div>
                            <div className="display-setting-page-input">
                                <SettingSelector
                                    key={curLanguage}
                                    className="rtc-setting-selector"
                                    defaultValue={curLanguage}
                                    options={Object.values(RemoI18nLanguagesEnum).map(value => ({
                                        label: t("settings.display-setting." + value),
                                        value,
                                        title: "",
                                    }))}
                                    onChange={onLanguageChanged}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </UserSettingLayoutContainer>
    );
};

export default DisplaySettingPage;
